/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * OrgField
 * --------
 * Label, control, and the one line underneath that is either an error or a
 * hint — never both, because two lines of small text under a box is where a
 * form starts to look busy.
 *
 * It owns the accessibility wiring, so every control in the wizard gets it
 * identically: a real <label for>, `aria-invalid` and `aria-describedby`
 * pointing at whichever line is showing. Children are a render function rather
 * than elements so the control receives the generated ids without the caller
 * having to thread them through.
 */

import React, { useId } from 'react';

export default function OrgField({ label, required, error, help, children }) {
  const id = useId();
  const messageId = `${id}-msg`;
  const invalid = !!error;
  // An error replaces the hint rather than stacking below it: once something is
  // wrong, the correction is the only thing worth reading.
  const described = invalid || help ? messageId : undefined;

  return (
    <div className="orgf-field">
      <label htmlFor={id}>
        {label}
        {required && <span className="orgf-req" aria-hidden="true">*</span>}
      </label>
      {children({ id, invalid, describedBy: described })}
      {invalid && <span className="orgf-err" id={messageId} role="alert">{error}</span>}
      {!invalid && help && <span className="orgf-help" id={messageId}>{help}</span>}
    </div>
  );
}
